import type { ImageModifiers } from '@nuxt/image';

import { joinURL } from 'ufo';

/**
 * Local defineProvider — mirrors @nuxt/image/runtime's defineProvider
 * without importing it.
 *
 * Importing directly from `@nuxt/image/runtime` pulls in `useImage`,
 * which collides with Nuxt's auto-import during the Nitro esbuild step
 * (duplicate "const useImage" in the same server chunk, especially
 * under pnpm symlinks). A local version avoids the issue entirely.
 *
 * Returns a lazy factory `() => ImageProvider<T>` — exactly like the
 * original — so the Nuxt Image runtime can call `provider()` to get
 * the `{ getImage }` object.
 */
interface ImageProvider<T = Record<string, unknown>> {
	getImage: (
		src: string,
		options: T & { modifiers: Record<string, unknown> }
	) => { url: string };
}

function defineProvider<T>(
	setup: ImageProvider<T> | (() => ImageProvider<T>)
): () => ImageProvider<T> {
	let result: ImageProvider<T>;
	return () => {
		if (result) return result;
		result =
			typeof setup === 'function' ? (setup as () => ImageProvider<T>)() : setup;
		return result;
	};
}

export function createMapper(map: any) {
	return (key?: string) => {
		return key ? map[key] || key : map.missingValue;
	};
}

// Esse método é uma cópia do método original do @nuxt/image. Estavamos tendo problema para usar o original, pois, ele vinha com coisas client-side e estamos usando no servidor também aqui.
export function createOperationsGenerator({
	formatter,
	keyMap,
	joinWith = '/',
	valueMap
}: any = {}) {
	if (!formatter) {
		formatter = (key: string, value: string | number) => `${key}=${value}`;
	}
	if (keyMap && typeof keyMap !== 'function') {
		keyMap = createMapper(keyMap);
	}
	const map = valueMap || {};
	Object.keys(map).forEach(valueKey => {
		if (typeof map[valueKey] !== 'function') {
			map[valueKey] = createMapper(map[valueKey]);
		}
	});

	return (modifiers: { [key: string]: any } = {}) => {
		const operations = Object.entries(modifiers)
			.filter(([_, value]) => typeof value !== 'undefined')
			.map(([key, value]) => {
				const mapper = map[key];
				if (typeof mapper === 'function') {
					value = mapper(modifiers[key]!);
				}

				key = typeof keyMap === 'function' ? keyMap(key) : key;

				return formatter!(key, value);
			});

		return operations.join(joinWith);
	};
}

interface S3Bucket {
	name: string;
	url: string;
}

interface ResizeOptions {
	width?: number;
	height?: number;
}

interface OverlayOptions {
	bucket: string;
	key: string;
	wRatio: number;
	hRatio: number;
	alpha: number;
}

interface ExtendedImageModifiers extends Partial<ImageModifiers> {
	overlayWith?: OverlayOptions;
	width?: number;
	height?: number;
}

interface ImageEdits {
	format?: string;
	quality?: number;
	blur?: number;
	resize?: ResizeOptions;
	rotate?: null;
	overlayWith?: OverlayOptions;
}

interface ImageConfig {
	bucket: string;
	key: string;
	edits: ImageEdits;
}

function normalizePathStyleS3(url: string): string {
	const match =
		/^https:\/\/s3([.-][a-z0-9-]+)?\.amazonaws\.com\/([^/]+)\/(.+)$/.exec(url);
	return match
		? `https://${match[2]}.s3${match[1] ?? ''}.amazonaws.com/${match[3]}`
		: url;
}

const S3_BUCKETS: S3Bucket[] = [
	{
		name: 'blintz-properties-sandbox',
		url: 'https://blintz-properties-sandbox.s3.amazonaws.com/'
	},
	{
		name: 'blintz-properties-sandbox',
		url: 'https://blintz-properties-sandbox.s3.us-east-2.amazonaws.com/'
	},
	{
		name: 'pilar-company-assets',
		url: 'https://pilar-company-assets.s3.amazonaws.com/'
	},
	{
		name: 'pilar-company-assets',
		url: 'https://pilar-company-assets.s3.us-east-2.amazonaws.com/'
	},
	{
		name: 'static.pilarhomes.com.br',
		url: 'https://static.pilarhomes.com.br/'
	},
	{
		name: 'static-staging.pilarhomes.com.br',
		url: 'https://static-staging.pilarhomes.com.br/'
	}
];

const LAMBDA_BASE_URL: string =
	process.env.NUXT_AWS_SERVERLESS_IMAGE_HANDLER_URL ||
	'https://imagens.pilarhomes.com.br';

const cloudfrontAvailableKeys = [
	'format',
	'quality',
	'blur',
	'width',
	'height',
	'overlayWith'
] as const;
const cloudfrontAvailableFormats = ['webp', 'png', 'jpg'] as const;
const numberValueKeys = ['quality', 'blur'] as const;

const operationsGenerator = createOperationsGenerator();

function getWatermarkedImageConfig(imageUrl: string): ImageConfig | null {
	const imageOptions = imageUrl.replace(LAMBDA_BASE_URL, '');

	const stringEdits = imageOptions.split('/')?.pop();

	if (!stringEdits) return null;

	const config = JSON.parse(atob(stringEdits));

	return {
		...(config || {}),
		edits: {
			...(config?.edits || {})
		}
	};
}

const isPlaceholder = ({
	modifiers,
	edits
}: {
	modifiers: { [key: string]: any };
	edits: ImageEdits;
}) => {
	return modifiers.blur || edits.blur;
};

function getModifiersWithMergedOverlay(
	modifiers: ExtendedImageModifiers,
	newOverlayWith: ExtendedImageModifiers['overlayWith']
) {
	if (!newOverlayWith) return modifiers;

	return {
		...modifiers,
		overlayWith: {
			...modifiers.overlayWith,
			...newOverlayWith
		}
	};
}

function cleanupImageBuffers() {
	// Forçar liberação de buffers de imagem
	if (global.gc) {
		// global.gc();
	}
}

/**
 * Core image processing logic extracted to support recursive calls
 * (e.g. when processing Lambda/watermarked URLs).
 */
function processImage(
	src: string,
	options: { modifiers?: ExtendedImageModifiers; baseUrl?: string }
): { url: string } {
	try {
		const { modifiers = {}, baseUrl } = options;

		// URLs path-style (https://s3[.região].amazonaws.com/<bucket>/<key>)
		// não casavam com o allowlist virtual-hosted e saíam CRUAS — fotos de
		// imóvel de até 4MB sem resize. Normaliza antes do lookup.
		const imageUrl = normalizePathStyleS3(src.trim());

		if (imageUrl.endsWith('.svg') || imageUrl.includes('localhost')) {
			return { url: imageUrl };
		}

		const isLambdaUrl = imageUrl.startsWith(LAMBDA_BASE_URL);

		const imageConfig = isLambdaUrl
			? getWatermarkedImageConfig(imageUrl)
			: null;

		const bucketIdentifier = isLambdaUrl ? imageConfig?.bucket : imageUrl;

		const bucket = S3_BUCKETS.find(
			({ url, name }) =>
				bucketIdentifier?.startsWith(url) || bucketIdentifier === name
		);

		if (!bucket) return { url: imageUrl };

		const imagePath = isLambdaUrl
			? imageConfig?.key
			: imageUrl.replace(bucket.url, '');

		if (!imagePath) return { url: imageUrl };

		// * O overlayWith do modifiers tem prioridade sobre o overlayWith do imageConfig
		const mergedModifiers = getModifiersWithMergedOverlay(
			modifiers as ExtendedImageModifiers,
			(modifiers as ExtendedImageModifiers).overlayWith ||
				imageConfig?.edits?.overlayWith
		);

		if (isLambdaUrl) {
			const originalImageUrl = joinURL(bucket.url, imagePath);

			return processImage(originalImageUrl, {
				modifiers: mergedModifiers as any,
				baseUrl
			});
		}

		const operations = operationsGenerator(modifiers).split('/');
		const edits: ImageEdits = imageConfig?.edits || {};

		for (const operation of operations) {
			const [key, value] = operation.split('=');

			if (
				!value ||
				!cloudfrontAvailableKeys.includes(
					key as (typeof cloudfrontAvailableKeys)[number]
				)
			) {
				continue;
			}
			if (key === 'width' || key === 'height') {
				const dimensionKey = key as keyof ResizeOptions;
				edits.resize ||= {};
				edits.resize[dimensionKey] = +value;
			} else if (key === 'format') {
				if (
					!cloudfrontAvailableFormats.includes(
						value as (typeof cloudfrontAvailableFormats)[number]
					)
				) {
					continue;
				}

				edits.format = value;
			} else if (
				numberValueKeys.includes(key as (typeof numberValueKeys)[number])
			) {
				const numericKey = key as 'quality' | 'blur';
				edits[numericKey] = +value;
			} else if (key === 'overlayWith') {
				edits.overlayWith =
					edits.overlayWith ??
					(modifiers as ExtendedImageModifiers).overlayWith;
			} else {
				// @ts-ignore
				edits[key as keyof ImageEdits] = value;
			}
		}

		edits.format ||= 'webp';
		edits.rotate ||= null; // Evita o bug de inversão de imagem em proporção vertical (https://github.com/aws-solutions/serverless-image-handler/issues/540)

		if (!Object.keys(edits).length) return { url: imageUrl };

		if (edits.overlayWith && isPlaceholder({ modifiers, edits })) {
			delete edits.overlayWith;
		}

		const imageRequest = JSON.stringify({
			bucket: bucket.name,
			key: imagePath,
			edits
		});

		const encodedImageRequest = btoa(imageRequest);

		setTimeout(cleanupImageBuffers, 100);

		return {
			url: joinURL(baseUrl || '', encodedImageRequest)
		};
	} catch (error) {
		console.error(error);

		return { url: src };
	}
}

export default defineProvider<{ baseUrl?: string }>({
	getImage(src, { modifiers, baseUrl }) {
		return processImage(src, {
			modifiers: modifiers as ExtendedImageModifiers,
			baseUrl
		});
	}
});
